import { EIM_STALE_MS, isBatteryOverheatAlert, isIdleAlert, type EimVehicle } from './useEimApi'

/**
 * EIM's own status colour table -- kept independent of the shared cc/tokens.ts Tone enum
 * because two of these six states need a hue outside that enum's five (see 'alert' and
 * 'charging' below). Centralised here, not duplicated across EimShell.tsx and
 * EimPortsMap.tsx separately, so the tile grid and the port map can never drift into
 * showing two different colours for the same vehicle state again -- which is exactly how
 * 'alert' ended up red on the map while the tile used the shared danger tone, before this.
 */
export type EimStatusKey = 'moving' | 'idle' | 'parked' | 'alert' | 'overheat' | 'charging' | 'offline' | 'breakdown'

export const EIM_STATUS_LABEL: Record<EimStatusKey, string> = {
  moving: 'Moving',
  idle: 'Idling',
  parked: 'Parked',
  alert: 'Idle alert',
  overheat: 'Battery overheat',
  charging: 'Charging',
  offline: 'Offline',
  breakdown: 'Down',
}

export interface EimStatusStyle {
  /** Solid fill -- map marker glyphs, KPI dots. */
  hex: string
  /** Darker ink for text on a light tint of `hex`. */
  text: string
  /** ~10%-alpha tint of `hex`, for tile/panel backgrounds. */
  bg: string
  /** ~26%-alpha tint of `hex`, for tile/panel borders. */
  border: string
}

/**
 * moving/idle/offline/parked stay in the same green/amber/grey family every other live
 * map in this console already uses. Three needed a colour OUTSIDE that shared vocabulary:
 *  - 'alert' (idling long enough to escalate, WITH real power draw -- see
 *    isMeaningfulIdle in useEimApi.ts) is BLUE, not red. A truck idling too long is
 *    wasting energy, not broken, and colouring it identically to "something is broken"
 *    read as a false alarm to ops.
 *  - 'charging' moved OFF blue (the colour it used until this file existed) specifically
 *    so it can never again be confused with 'alert' -- the two are opposite situations
 *    (charging is doing exactly what it should; an idle alert needs a look), and sharing
 *    one hue between them defeated the point of colour-coding at all. Teal, used nowhere
 *    else in this state table.
 *  - 'breakdown' is the RED this file's own comment used to reserve for it, back when
 *    EIM had no breakdown concept at all. It does now (site_breakdown, same schema STAR
 *    and MBMT use) -- an open breakdown always wins over 'alert': a truck marked down is
 *    a known, human-confirmed fact, and showing it as merely "idling too long" would bury
 *    the more serious, already-actioned state under the less serious one.
 *  - 'overheat' (BATTERY_OVERHEAT, sustained max_cell_temp >= 45C) is a distinct orange/red
 *    shade from BOTH 'alert' (idle, routine/wasteful but not unsafe) and 'breakdown' (a
 *    human already knows and is handling it) -- it needs to read as more urgent than an
 *    idle alert without being confused for an already-actioned breakdown.
 */
export const EIM_STATUS_STYLE: Record<EimStatusKey, EimStatusStyle> = {
  moving: { hex: '#16A34A', text: '#047857', bg: 'rgba(16,185,129,0.10)', border: 'rgba(16,185,129,0.26)' },
  idle: { hex: '#D97706', text: '#B45309', bg: 'rgba(245,158,11,0.10)', border: 'rgba(245,158,11,0.26)' },
  parked: { hex: '#64748B', text: '#475569', bg: 'rgba(100,116,139,0.10)', border: 'rgba(100,116,139,0.22)' },
  alert: { hex: '#2563EB', text: '#1D4ED8', bg: 'rgba(37,99,235,0.10)', border: 'rgba(37,99,235,0.26)' },
  overheat: { hex: '#EA580C', text: '#9A3412', bg: 'rgba(234,88,12,0.10)', border: 'rgba(234,88,12,0.26)' },
  charging: { hex: '#0D9488', text: '#0F766E', bg: 'rgba(13,148,136,0.10)', border: 'rgba(13,148,136,0.26)' },
  offline: { hex: '#94A3B8', text: '#64748B', bg: 'rgba(148,163,184,0.10)', border: 'rgba(148,163,184,0.22)' },
  breakdown: { hex: '#DC2626', text: '#9F1239', bg: 'rgba(244,63,94,0.10)', border: 'rgba(244,63,94,0.26)' },
}

/** The single place that turns a vehicle's raw status + staleness + idle duration into
 *  the one status key every EIM surface (tile grid, port map, KPI strip) colours off.
 *  isDown is optional (defaults to "nothing is down") so every existing call site that
 *  predates breakdown tracking keeps working unchanged -- only callers that actually have
 *  breakdown state to offer need to pass it. */
export function eimStatusKey(
  v: EimVehicle, now: number, isDown?: (site: EimVehicle['site'], vehicleId: string) => boolean,
): EimStatusKey {
  const staleMs = now - new Date(v.polled_ts).getTime()
  if (staleMs > EIM_STALE_MS) return 'offline'
  if (isDown?.(v.site, v.vehicle_id)) return 'breakdown'
  if (isBatteryOverheatAlert(v, now)) return 'overheat'
  if (isIdleAlert(v, now)) return 'alert'
  if (v.status === 'charging') return 'charging'
  if (v.status === 'parked') return 'parked'
  return v.status === 'idle' ? 'idle' : 'moving'
}
